import { AgentMode } from "@/components/ai/prompt-input/agent-mode";
import { cn } from "@/components/podkit/lib/cn";
import { forwardRef, type HTMLAttributes, type ReactNode } from "react";

export const specModeBorderClass =
    "border border-dashed border-content-brand-accent-01/50 transition-colors focus-within:border-content-brand-accent-01";

const getModeBorderClass = (mode: AgentMode, isActive: boolean): string => {
    switch (mode) {
        case AgentMode.Spec:
            return isActive
                ? "border-dashed border-border-brand-accent-01-active"
                : "border-dashed border-border-brand-accent-01";
        case AgentMode.Ralph:
            return isActive ? "border-border-orange-active" : "border-border-orange";
        case AgentMode.Agent:
        default:
            return isActive ? "border-border-strong" : "";
    }
};

type ChatInputShellProps = {
    isDragging: boolean;
    dragHandlers: HTMLAttributes<HTMLDivElement>;
    agentMode?: AgentMode;
    isActive?: boolean;
    /** @deprecated Use agentMode instead */
    isSpecMode?: boolean;
    className?: string;
    children: ReactNode;
    onFocus?: (e: React.FocusEvent) => void;
    onBlur?: (e: React.FocusEvent) => void;
    onMouseEnter?: () => void;
    onMouseLeave?: () => void;
};

export const ChatInputShell = forwardRef<HTMLDivElement, ChatInputShellProps>(
    (
        {
            isDragging,
            dragHandlers,
            agentMode,
            isActive = false,
            isSpecMode,
            className,
            children,
            onFocus,
            onBlur,
            onMouseEnter,
            onMouseLeave,
        },
        ref,
    ) => {
        // Support both new agentMode prop and legacy isSpecMode prop
        const borderClass =
            agentMode !== undefined
                ? getModeBorderClass(agentMode, isActive)
                : isSpecMode
                  ? specModeBorderClass
                  : "border border-border-base";

        return (
            <div
                ref={ref}
                onFocus={onFocus}
                onBlur={onBlur}
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
                className={cn(
                    "relative w-full min-w-0 rounded-lg border border-border-base bg-surface-pure",
                    "transition-[border-color,box-shadow] ease-out",
                    isActive ? "duration-150" : "delay-150 duration-150",
                    isActive && "shadow-sm",
                    borderClass,
                    isDragging && "ring-2 ring-content-accent ring-offset-2",
                )}
                {...dragHandlers}
            >
                <div
                    className={cn(
                        "flex flex-col gap-2 overflow-hidden pb-2 px-4 pt-4",
                        className,
                    )}
                >
                    {children}
                </div>
            </div>
        );
    },
);

ChatInputShell.displayName = "ChatInputShell";
